import { Box, Button, Divider, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { getSupplyId } from "../helpers/getSupplyId";
import { updateSupplyProvider } from "../helpers/updateSupplyProvider";
import { useModals } from "../../../../context/ModalContext";

export const EditSupplyXProvider = ({ modalKey, supply, onClose, onSuccess }) => {
  const [supplyBase, setSupplyBase] = useState(null);
  const token = sessionStorage.getItem("token");
  const { closeByKey } = useModals()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    const fetchBase = async () => {
      try {
        const res = await getSupplyId({ id: supply.supplyId, token });
        setSupplyBase(res.data);
        setValue("name", supply.name);
        const priceNum = Number(supply.price);
        setValue("price", isNaN(priceNum) ? 0 : parseFloat(priceNum.toFixed(2)));
        setValue("description", supply.description);
        res.data.requiredProperties.forEach((prop) => {
          const value = supply.requiredProperties?.[prop.field] ?? "";
          setValue(prop.field, value);
        });
      } catch (error) {
        console.log("Error loading supply base:", error);
      }
    };

    if (supply?.supplyId) {
      fetchBase();
    }
  }, [supply, setValue, token]);

  const onSubmit = async (data) => {
    const payload = {
      name: data.name,
      price: Number(data.price),
      description: data.description,
      requiredProperties: {},
    };

    supplyBase?.requiredProperties?.forEach((prop) => {
      payload.requiredProperties[prop.field] = Number(data[prop.field] || 0);
    });

    try {
      await updateSupplyProvider({ token, data: payload, id: supply.id });
      closeByKey(modalKey)
    } catch (error) {
      console.error("Error updating supply:", error);
    }
  };

  if (!supplyBase) {
    return <Typography>Loading...</Typography>;
  }


  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100%",
        p: { xs: 2, md: 4 },
      }}
    >
      <Box
        sx={{
          p: 4,
          borderRadius: 3,
          width: "100%",
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant="h5"
          sx={{ mb: 4, fontWeight: "700", textAlign: "center" }}
        >
          Edit Supply for Provider
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            alignItems: "flex-start",
          }}
        >
          {/* Formulario */}
          <Box sx={{ flex: 1 }}>
            <TextField
              label="Name"
              {...register("name", { required: "Name is required" })}
              error={!!errors.name}
              helperText={errors.name?.message}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            />

            <TextField
              label="Price"
              type="number"
              inputProps={{ step: "0.01", min: 0 }}
              {...register("price", {
                required: "Price is required",
                min: { value: 0.01, message: "Price must be > 0" },
              })}
              error={!!errors.price}
              helperText={errors.price?.message}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            />

            <TextField
              label="Description"
              multiline
              rows={3}
              {...register("description", {
                required: "Description is required",
              })}
              error={!!errors.description}
              helperText={errors.description?.message}
              fullWidth
              size="small"
              sx={{ mb: 3 }}
            />

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Required Properties
            </Typography>

            {supplyBase.requiredProperties.map((prop) => (
              <TextField
                key={prop.field}
                label={prop.label}
                type="number"
                inputProps={{ step: "any", min: 0 }}
                {...register(prop.field, {
                  required: `${prop.label} is required`,
                  min: { value: 0, message: "Must be ≥ 0" },
                })}
                error={!!errors[prop.field]}
                helperText={errors[prop.field]?.message}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
              />
            ))}

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button onClick={onClose} sx={{ mr: 2, px: 4 }}>
                Cancel
              </Button>
              <Button variant="contained" type="submit" sx={{ px: 4 }}>
                Save Changes
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              width: 190,
              height: 190,
              borderRadius: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {supplyBase.image ? (
              <img
                src={supplyBase.image}
                alt={supplyBase.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
                loading="lazy"
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                No Image Available
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
